import { getAutoPanDelta } from '../core';
import type { MindmapStoreApi } from '../store';
import { rendererPoint } from '../utils';

/** A running auto-pan loop: feed it every pointer move, stop it when the drag ends. */
export type AutoPan<E extends MouseEvent> = { update: (event: E) => void; stop: () => void };

/**
 * While a drag is held near the pane edge, pans the view a little every animation frame — even when the pointer
 * stands still — so the canvas and its dotted background scroll along (v11 `autoPanOnNodeDrag` / `autoPanOnConnect`).
 * `onPan` gets the latest pointer event after each pan so the dragged item can follow it into the newly shown canvas.
 */
export const startAutoPan = <E extends MouseEvent>(store: MindmapStoreApi, renderer: Element, onPan: (event: E) => void): AutoPan<E> => {
  const view = renderer.ownerDocument.defaultView;
  let latest: E | null = null;
  let frame = 0;

  const step = () => {
    const { viewport, paneSize, setViewport } = store.getState();
    const delta = latest ? getAutoPanDelta(rendererPoint(renderer, latest), paneSize) : null;
    if (latest && delta && (delta.x !== 0 || delta.y !== 0)) {
      setViewport({ ...viewport, x: viewport.x + delta.x, y: viewport.y + delta.y });
      onPan(latest);
    }
    frame = view?.requestAnimationFrame(step) ?? 0;
  };
  frame = view?.requestAnimationFrame(step) ?? 0;

  return {
    update: (event) => {
      latest = event;
    },
    stop: () => view?.cancelAnimationFrame(frame),
  };
};
